import React, {
  useContext,
  createContext,
  useMemo,
  useState,
  useEffect,
} from 'react';
import {
  useCurrentAccountList,
  useCurrentCompanyList,
  useCurrentUser,
  useSpoof,
} from 'hooks/api';
import { IAccount } from 'services/account';
import { Business } from 'models/business';
import { AuthService } from 'services';

const AuthContext = createContext<{
  isLoadingFormationsAuth: boolean;
  currentUserId: string | undefined;
  currentAccountId: string | undefined;
  currentAccount: IAccount | undefined;
  currentCompanyId: string | undefined;
  currentCompany: Business | undefined;
  updateCurrentAccountId: (accountId: string) => void;
  updateCurrentCompanyId: (companyId: string) => void;
  userAccounts: IAccount[];
  userBusinesses: Business[];
  handleSwitchCompany: (companyId: string, switchToAccountId?: string) => void;
}>({
  isLoadingFormationsAuth: false,
  currentUserId: undefined,
  currentAccountId: undefined,
  currentAccount: undefined,
  currentCompanyId: undefined,
  currentCompany: undefined,
  updateCurrentAccountId: () => {},
  updateCurrentCompanyId: () => {},
  userAccounts: [],
  userBusinesses: [],
  handleSwitchCompany: () => {},
});

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { spoofUserID } = useSpoof();
  const { currentUser, isLoading: isLoadingUser } = useCurrentUser();
  const { accounts: userAccounts, isLoading: isLoadingAccounts } =
    useCurrentAccountList(currentUser?.id);
  const { companies: userBusinesses, isLoading: isLoadingBusinesses } =
    useCurrentCompanyList(currentUser?.id);

  const [currentAccountId, setCurrentAccountId] = useState<string | null>(() =>
    AuthService.currentCustomerAccountId(),
  );
  const [currentCompanyId, setCurrentCompanyId] = useState<string | null>(() =>
    AuthService.currentCustomerCompanyId(),
  );

  const updateCurrentAccountId = (accountId: string) => {
    AuthService.currentCustomerAccountId(accountId);
    setCurrentAccountId(accountId);
  };

  const updateCurrentCompanyId = (companyId: string) => {
    AuthService.currentCustomerCompanyId(companyId);
    setCurrentCompanyId(companyId);
  };

  useEffect(() => {
    if (spoofUserID) {
      const account = userAccounts.find(
        (acc) => acc.ownerUserId === spoofUserID,
      );
      if (account) {
        updateCurrentAccountId(account.id);
      }
      if (userBusinesses.length > 0) {
        updateCurrentCompanyId(userBusinesses[0].id);
      }
    }
  }, [spoofUserID, userAccounts, userBusinesses]);

  const currentAccount = useMemo(() => {
    if (currentAccountId) {
      return userAccounts.find((account) => account.id === currentAccountId);
    }
    if (userAccounts && userAccounts.length > 0) {
      updateCurrentAccountId(userAccounts[0].id);
      return userAccounts[0];
    }
    return undefined;
  }, [userAccounts, currentAccountId]);

  const currentCompany = useMemo(() => {
    if (currentCompanyId) {
      const company = userBusinesses.find(
        (business) => business.id === currentCompanyId,
      );
      return company;
    }
    if (userBusinesses && userBusinesses.length > 0) {
      updateCurrentCompanyId(userBusinesses[0].id);
      return userBusinesses[0];
    }
    return undefined;
  }, [userBusinesses, currentCompanyId]);

  const handleSwitchCompany = (
    companyId: string,
    switchToAccountId?: string,
  ) => {
    const accountId =
      switchToAccountId ||
      userBusinesses.find((business) => business.id === companyId)?.accountId;
    if (accountId) {
      updateCurrentCompanyId(companyId);
      window.location.reload();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isLoadingFormationsAuth:
          isLoadingUser || isLoadingAccounts || isLoadingBusinesses,
        currentUserId: currentUser?.id,
        currentAccountId: currentAccountId || undefined,
        currentCompanyId: currentCompanyId || undefined,
        currentAccount,
        currentCompany,
        updateCurrentAccountId,
        updateCurrentCompanyId,
        userAccounts,
        userBusinesses,
        handleSwitchCompany,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line import/no-default-export
export default AuthProvider;

export const useFormationsAuth = () => useContext(AuthContext);
